import React from "react";
import { getLogoSrc } from "../utils/logoHelper";
import { formatGameDate } from "../utils/dateFormatter";
import api from "../services/api";

const API_BASE_URL = api.defaults.baseURL.replace("/api", "");
const WIDTH = 1080;
const HEIGHT = 1080;

// Panel sits at y=540, circles centered on its top edge (y=440 from canvas top)
const PANEL_TOP = 540;
const CIRCLE_SIZE = 200;
const CIRCLE_TOP = PANEL_TOP - CIRCLE_SIZE / 2; // 440
const HOME_CIRCLE_LEFT = 216;
const AWAY_CIRCLE_LEFT = 664;

function GraphicRecapClean({
  graphicData,
  teamSport,
  teamColors,
  backgroundStyle = "linear-gradient",
  homeTeamLogo,
  awayTeamLogoPreview,
  showWatermark = true,
  dominantImage = null,
  heroFont = "Bebas Neue",
  heroFontSize = null,
  accentBlockColor = null,
  detailTextColor = null,
  dominantImageX = null,
  dominantImageY = null,
  dominantImageScale = null,
  dateFormat = "full",
}) {
  const {
    homeTeam,
    homeScore,
    awayScore,
    awayTeam,
    gameDate,
    topPerformer,
    topPerformerStats,
  } = graphicData;

  const homeWon = parseInt(homeScore) > parseInt(awayScore);
  const isTie = parseInt(homeScore) === parseInt(awayScore);
  const resultLabel = isTie ? "TIE GAME" : homeWon ? "VICTORY" : "FINAL";

  const backgroundPhoto = graphicData.backgroundPhoto || null;
  const bgImageScale = (graphicData.bgImageScale ?? 100) / 100;
  const bgImageX = graphicData.bgImageX ?? 50;
  const bgImageY = graphicData.bgImageY ?? 50;
  const heroFontFamily = `'${heroFont || "Bebas Neue"}', 'Arial Narrow', Arial, sans-serif`;
  const barlowFamily =
    "'Barlow Condensed', 'Barlow', 'Arial Narrow', Arial, sans-serif";
  const primaryColor = teamColors.primary;
  const secondaryColor = teamColors.secondary || teamColors.primary;
  const scoreFontSize = 160;
  const resultFontSize = Number(heroFontSize) || 140;
  const accent = accentBlockColor || primaryColor;
  const detailColor = detailTextColor || "rgba(0,0,0,0.5)";

  const sportEmoji =
    teamSport === "Softball"
      ? "🥎"
      : teamSport === "Basketball"
        ? "🏀"
        : teamSport === "Soccer"
          ? "⚽"
          : "⚾";

  const getBackground = () => {
    switch (backgroundStyle) {
      case "linear-gradient":
        return `linear-gradient(135deg, ${primaryColor} 0%, ${secondaryColor} 100%)`;
      case "radial-gradient":
        return `radial-gradient(circle at center, ${primaryColor} 0%, ${secondaryColor} 100%)`;
      case "diagonal-gradient":
        return `linear-gradient(45deg, ${primaryColor} 0%, ${secondaryColor} 100%)`;
      case "split-two-tone":
        return `linear-gradient(90deg, ${primaryColor} 50%, ${secondaryColor} 50%)`;
      case "solid-primary":
        return primaryColor;
      case "solid-secondary":
        return secondaryColor;
      default:
        return `linear-gradient(135deg, ${primaryColor} 0%, ${secondaryColor} 100%)`;
    }
  };

  return (
    <div
      id="graphic-recap"
      style={{
        position: "relative",
        width: WIDTH,
        height: HEIGHT,
        overflow: "hidden",
        borderRadius: 16,
        background: getBackground(),
        fontFamily: heroFontFamily,
      }}
    >
      {/* ── LAYER 1 — Background photo (full bleed, optional) ── */}
      {backgroundPhoto && (
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: WIDTH,
            height: HEIGHT,
            overflow: "hidden",
            zIndex: 1,
          }}
        >
          <img
            src={backgroundPhoto}
            alt=""
            crossOrigin="anonymous"
            style={{
              position: "absolute",
              top: `${bgImageY}%`,
              left: `${bgImageX}%`,
              transform: `translate(-50%, -50%) scale(${bgImageScale})`,
              transformOrigin: "center center",
              width: "auto",
              height: "auto",
              minWidth: "100%",
              minHeight: "100%",
              maxWidth: "none",
            }}
          />
          {/* Dark scrim */}
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: WIDTH,
              height: HEIGHT,
              background: "rgba(0,0,0,0.30)",
            }}
          />
        </div>
      )}

      {/* ── LAYER 2 — Bottom white info panel ── */}
      <div
        style={{
          position: "absolute",
          bottom: 0,
          left: 0,
          width: WIDTH,
          height: HEIGHT / 2,
          background: "#ffffff",
          border: `4px solid ${primaryColor}`,
          boxSizing: "border-box",
          zIndex: 2,
        }}
      />

      {/* ── LAYER 3 — Home circle cutout ── */}
      <div
        style={{
          position: "absolute",
          top: CIRCLE_TOP,
          left: HOME_CIRCLE_LEFT,
          width: CIRCLE_SIZE,
          height: CIRCLE_SIZE,
          borderRadius: "50%",
          background: "#ffffff",
          border: `4px solid ${accent}`,
          zIndex: 3,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          overflow: "hidden",
        }}
      >
        {/* ── LAYER 4 — Home logo ── */}
        {homeTeamLogo ? (
          <img
            src={getLogoSrc(homeTeamLogo, API_BASE_URL)}
            alt={homeTeam || "Home"}
            crossOrigin="anonymous"
            style={{ height: 140, width: "auto", maxWidth: 160, zIndex: 4 }}
          />
        ) : (
          <span style={{ fontSize: 80, lineHeight: 1, zIndex: 4 }}>
            {sportEmoji}
          </span>
        )}
      </div>

      {/* ── LAYER 3 — Away circle cutout ── */}
      <div
        style={{
          position: "absolute",
          top: CIRCLE_TOP,
          left: AWAY_CIRCLE_LEFT,
          width: CIRCLE_SIZE,
          height: CIRCLE_SIZE,
          borderRadius: "50%",
          background: "#ffffff",
          border: `4px solid ${accent}`,
          zIndex: 3,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          overflow: "hidden",
        }}
      >
        {/* ── LAYER 4 — Away logo ── */}
        {awayTeamLogoPreview ? (
          <img
            src={awayTeamLogoPreview}
            alt={awayTeam || "Away"}
            crossOrigin="anonymous"
            style={{ height: 140, width: "auto", maxWidth: 160, zIndex: 4 }}
          />
        ) : (
          <span style={{ fontSize: 80, lineHeight: 1, zIndex: 4 }}>
            {sportEmoji}
          </span>
        )}
      </div>

      {/* ── LAYER 5 — VS between circles ── */}
      <div
        style={{
          position: "absolute",
          top: 490,
          left: 540,
          transform: "translateX(-50%)",
          fontFamily: heroFontFamily,
          fontSize: 64,
          color: accent,
          lineHeight: 1,
          zIndex: 5,
        }}
      >
        VS
      </div>

      {/* ── LAYER 6 — Score row ── */}
      <div
        style={{
          position: "absolute",
          top: 690,
          left: 540,
          transform: "translateX(-50%)",
          display: "flex",
          alignItems: "baseline",
          zIndex: 6,
        }}
      >
        <span
          style={{
            fontFamily: heroFontFamily,
            fontSize: scoreFontSize,
            color: accent,
            lineHeight: 1,
          }}
        >
          {homeScore ?? "0"}
        </span>
        <span
          style={{
            fontFamily: heroFontFamily,
            fontSize: 100,
            color: "rgba(0,0,0,0.3)",
            lineHeight: 1,
            margin: "0 8px",
          }}
        >
          -
        </span>
        <span
          style={{
            fontFamily: heroFontFamily,
            fontSize: scoreFontSize,
            color: "rgba(0,0,0,0.4)",
            lineHeight: 1,
          }}
        >
          {awayScore ?? "0"}
        </span>
      </div>

      {/* ── LAYER 7 — Home team name ── */}
      <div
        style={{
          position: "absolute",
          top: 660,
          left: HOME_CIRCLE_LEFT,
          width: CIRCLE_SIZE,
          textAlign: "center",
          fontFamily: heroFontFamily,
          fontSize: 36,
          color: accent,
          lineHeight: 1,
          zIndex: 7,
        }}
      >
        {homeTeam || "YOUR TEAM"}
      </div>

      {/* ── LAYER 7 — Away team name ── */}
      <div
        style={{
          position: "absolute",
          top: 660,
          left: AWAY_CIRCLE_LEFT,
          width: CIRCLE_SIZE,
          textAlign: "center",
          fontFamily: heroFontFamily,
          fontSize: 36,
          color: "rgba(0,0,0,0.7)",
          lineHeight: 1,
          zIndex: 7,
        }}
      >
        {awayTeam || "OPPONENT"}
      </div>

      {/* ── LAYER 8 — Result label (top half, above white panel) ── */}
      <div
        style={{
          position: "absolute",
          top: 160,
          left: 0,
          width: WIDTH,
          textAlign: "center",
          fontFamily: heroFontFamily,
          fontSize: resultFontSize,
          color: "#ffffff",
          WebkitTextStroke: "3px rgba(0,0,0,0.8)",
          letterSpacing: 10,
          lineHeight: 1,
          whiteSpace: "nowrap",
          zIndex: 10,
        }}
      >
        {resultLabel}
      </div>

      {/* ── LAYER 9 — Player of the game ── */}
      {topPerformer && (
        <>
          <div
            style={{
              position: "absolute",
              top: 860,
              left: 540,
              transform: "translateX(-50%)",
              fontFamily: barlowFamily,
              fontSize: 28,
              color: detailColor,
              letterSpacing: 4,
              whiteSpace: "nowrap",
              zIndex: 9,
            }}
          >
            PLAYER OF THE GAME
          </div>
          <div
            style={{
              position: "absolute",
              top: 895,
              left: 540,
              transform: "translateX(-50%)",
              fontFamily: heroFontFamily,
              fontSize: 60,
              color: accent,
              lineHeight: 1,
              whiteSpace: "nowrap",
              zIndex: 9,
            }}
          >
            {topPerformer}
          </div>
          {topPerformerStats && (
            <div
              style={{
                position: "absolute",
                top: 960,
                left: 540,
                transform: "translateX(-50%)",
                fontFamily: barlowFamily,
                fontSize: 36,
                color: detailColor,
                whiteSpace: "nowrap",
                zIndex: 9,
              }}
            >
              {topPerformerStats}
            </div>
          )}
        </>
      )}

      {/* ── LAYER 9 — Game date (always) ── */}
      {gameDate && (
        <div
          style={{
            position: "absolute",
            bottom: 20,
            left: 540,
            transform: "translateX(-50%)",
            fontFamily: barlowFamily,
            fontSize: 32,
            color: detailColor,
            whiteSpace: "nowrap",
            zIndex: 9,
          }}
        >
          {formatGameDate(gameDate, dateFormat)}
        </div>
      )}

      {/* ── LAYER 10 — Watermark ── */}
      {showWatermark && (
        <div
          style={{
            position: "absolute",
            bottom: 12,
            right: 12,
            zIndex: 10,
            background: 'rgba(255,255,255,0.92)',
            padding: '6px 12px',
            borderRadius: 8,
            pointerEvents: 'none',
          }}
        >
          <img src="/PMlogoleft.png" alt="Playmaker Media Kit" style={{ height: 80, width: 'auto', display: 'block' }} />
        </div>
      )}
    </div>
  );
}

export default GraphicRecapClean;
